import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {DateTime} from 'luxon';
import ReactSelect from 'react-select';
import AddNewsModal from '../Components/AddNewsModal';
import {useDeleteNews, useGetNews} from "../Hooks/NewsHooks";
import {ColumnDef} from "@tanstack/react-table";
import ActionIcon from "../../../Components/ActionIcon";
import TableMetronic from "../../../app/common/components/table/TableMetronic";
import UpdateNewsModal from "../Components/UpdateNewsModal";
import Button, {faIconAdd} from "../../../Components/Button";
import FormModal from "../../../Components/FormModal";
import {News} from "../Types/type";

type TypeFilter = 'all' | 'encart' | 'popup';
type StatusFilter = 'all' | 'ongoing';

interface FilterOption<T> {
  value: T;
  label: string;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const NewsPage = (): JSX.Element => {
  const intl = useIntl();

  const [SelectedNews, setSelectedNews] = useState<News | undefined>();
  const [ShowEditeModal, setShowEditeModal] = useState(false);
  const [show, setShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const {
    isLoading,
    data: newsList,
  } = useGetNews();

  const typeOptions: FilterOption<TypeFilter>[] = [
    {value: 'all', label: 'Tous les types'},
    {value: 'encart', label: intl.formatMessage({id: 'ADMIN.NEWS.FORM.TYPE.ENCART'})},
    {value: 'popup', label: intl.formatMessage({id: 'ADMIN.NEWS.FORM.TYPE.POPUP'})},
  ];

  const statusOptions: FilterOption<StatusFilter>[] = [
    {value: 'all', label: 'Tous les statuts'},
    {value: 'ongoing', label: 'En cours'},
  ];

  const filteredNews = useMemo(() => {
    const list = newsList?.data ?? [];
    if (typeFilter === 'all' && statusFilter === 'all') return list;

    const today = DateTime.now().startOf('day');

    return list.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;

      if (statusFilter === 'ongoing') {
        const start = item.startDate ? DateTime.fromISO(item.startDate).startOf('day') : null;
        const end = item.endDate ? DateTime.fromISO(item.endDate).startOf('day') : null;
        const afterStart = !start || today >= start;
        const beforeEnd = !end || today <= end;
        if (!afterStart || !beforeEnd) return false;
      }

      return true;
    });
  }, [newsList, typeFilter, statusFilter]);

  const {mutate} = useDeleteNews();

  const handleDeleteNews = async () => {
    if (SelectedNews) {
      setShowDeleteModal(false);
      mutate(SelectedNews.id)
    }
  };

  const columns = useMemo<ColumnDef<News>[]>(
    () => {
      return [
        {
          header: 'Catégorie',
          accessorKey: 'category.name',
          size: 250,
        },
        {
          header: 'Type',
          accessorKey: 'type',
          size: 85,
          cell: (info) =>
            info.getValue() === 'popup'
              ? intl.formatMessage({id: 'ADMIN.NEWS.FORM.TYPE.POPUP'})
              : intl.formatMessage({id: 'ADMIN.NEWS.FORM.TYPE.ENCART'}),
        },
        {
          header: 'Texte',
          accessorKey: 'title',
        },
        {
          header: 'Lien',
          accessorKey: 'news',
        },
        {
          header: 'Date debut',
          accessorKey: 'startDate',
          cell: (info) => {
            const value = info.getValue<string | null>();
            return value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '';
          },
        },
        {
          header: 'Date fin',
          accessorKey: 'endDate',
          cell: (info) => {
            const value = info.getValue<string | null>();
            return value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '';
          },
        },
        {
          header: 'Action',
          size: 90,
          cell: (info) => (
            <div>
              <ActionIcon label={intl.formatMessage({id: 'ADMIN.NEWS.DELETE'})} faIcon='fs-1 fa-solid fa-trash'
                          onClick={() => {
                            setSelectedNews(info.row.original);
                            setShowDeleteModal(true)
                          }}/>

              <ActionIcon label={'Modifier la news'} faIcon={'fs-1 fa-solid fa-pencil'} onClick={() => {
                setSelectedNews(info.row.original);
                setShowEditeModal(true);
              }}/>
            </div>
          ),
          enableSorting: false,
        },
      ];
    },
    [setShowDeleteModal, intl]
  );

  const handleClose = () => setShow(false);

  const handleShow = () => {
    setShow(true);
  };

  return (
    <>
      <FormModal onSubmit={handleDeleteNews} show={showDeleteModal} onHide={() => setShowDeleteModal(false)}
                 submitLabel={'Supprimer'} title={'Suppression'}>
        <div>
          <p>Vous êtes sur le point de supprimer cette news. Cette opération est définitive et ne peut pas être annulée.
            Confirmez-vous la suppression ?</p>
        </div>
      </FormModal>
      <AddNewsModal
        show={show}
        hide={() => {
          handleClose();
        }}
      />
      <TableMetronic<News>
        data={filteredNews}
        columns={columns}
        isLoading={isLoading}
        title={intl.formatMessage({id: 'ADMIN.NEWS.LIST.TITLE'})}
        pagination={true}
        smallSearchBar={true}
        stackedHeader
        toolbar={
          <>
            <div style={{minWidth: 200, maxWidth: 260}}>
              <ReactSelect<FilterOption<TypeFilter>>
                options={typeOptions}
                value={typeOptions.find((o) => o.value === typeFilter)}
                onChange={(opt) => setTypeFilter(opt?.value ?? 'all')}
                isSearchable={false}
                classNamePrefix="filter-select"
                menuPortalTarget={document.body}
                styles={filterSelectStyles}
              />
            </div>
            <div style={{minWidth: 200, maxWidth: 260}}>
              <ReactSelect<FilterOption<StatusFilter>>
                options={statusOptions}
                value={statusOptions.find((o) => o.value === statusFilter)}
                onChange={(opt) => setStatusFilter(opt?.value ?? 'all')}
                isSearchable={false}
                classNamePrefix="filter-select"
                menuPortalTarget={document.body}
                styles={filterSelectStyles}
              />
            </div>
          </>
        }
        toolbarEnd={<Button onClick={handleShow} faIcon={faIconAdd} label='Ajouter'/>}
      />
      {SelectedNews && (
        <>
          <UpdateNewsModal
            showModal={ShowEditeModal}
            setShowModal={setShowEditeModal}
            news={SelectedNews}
          />
        </>
      )}
    </>
  );
};

export default NewsPage;
